/**
 * Navratri Giveaway 2026 — central campaign config (master handoff).
 *
 * Update only this file for schedule tweaks, six contest URLs, or emergency pageMode override.
 * TRIAL: /navratri-trial/ always TRIAL_LIVE — no date gates.
 */

export type NavratriPageMode =
  | 'auto'
  | 'force_preview'
  | 'force_r1'
  | 'force_r2'
  | 'force_r3'
  | 'force_completed'
  | 'trial_live'

export type NavratriState =
  | 'PRE_EVENT'
  | 'ROUND_1_LIVE'
  | 'BETWEEN_1_2'
  | 'ROUND_2_LIVE'
  | 'BETWEEN_2_3'
  | 'ROUND_3_LIVE'
  | 'COMPLETED'
  | 'TRIAL_LIVE'
  | 'LIVE_UNAVAILABLE'

export interface RoundConfig {
  label: string
  displayDateTa: string
  start: string
  end: string
  /** YouTube schedule reference only — website LIVE still uses `start` (e.g. R1 video 06:00, page 07:00). */
  youtubePublish?: string
  /** Full YouTube watch/youtu.be URL — or placeholder {{ROUND_N_VIDEO_URL}} until handoff. */
  videoUrl: string
  /** Google Forms docs.google.com viewform URL for iframe embed — or placeholder until handoff. */
  formUrl: string
  /** Optional forms.gle (or other) short link for direct-open fallback. */
  formShareUrl?: string
  formHeightMobile: number
  formHeightTablet: number
  formHeightDesktop: number
}

export interface NavratriGiveawayConfig {
  pageMode: NavratriPageMode
  timezone: 'Asia/Kolkata'
  checkIntervalMs: number
  resyncClockMs: number
  rounds: [RoundConfig, RoundConfig, RoundConfig]
  privacyTextTa: string
  rulesSummaryTa: string
  rulesTextTa: string
  winnerAnnouncementTextTa: string
  recommendedContentIds: string[]
  recommendedYoutube: { titleTa: string; titleEn: string; videoId: string }[]
}

/** Round 1 production assets (Oct 12 2026). */
export const ROUND_1_VIDEO_URL = 'https://youtu.be/QGilkQAGZ4M'
export const ROUND_1_FORM_VIEW_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSccXdqcCO_RqeedrLHwK4w2aNhitKWfzpcoyX2NK_rQQdYhKA/viewform'
export const ROUND_1_FORM_SHARE_URL = 'https://forms.gle/p7DaHuYHyVwXymPX9'
/** YouTube public 06:00 IST; website/Form LIVE at `start` 07:00 IST — intentional gap. */
export const ROUND_1_YOUTUBE_PUBLISH = '2026-10-12T06:00:00+05:30'

/** Round 2 production assets. Website LIVE matches the YouTube public time. */
export const ROUND_2_VIDEO_URL = 'https://youtu.be/Um-z0oV_yQ0'
export const ROUND_2_FORM_VIEW_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSdWAQBGLVd5T-JcCJWe-ya8_FLGO5wkc3EBgD7gMJpOk0Gxsg/viewform'
export const ROUND_2_FORM_SHARE_URL = 'https://forms.gle/dZKXrSy8ddjrFnNx7'
export const ROUND_2_YOUTUBE_PUBLISH = '2026-10-16T07:00:00+05:30'

/** Round 3 production assets. Embed URL is the forms.gle redirect target (same method as Round 2). */
export const ROUND_3_VIDEO_URL = 'https://youtu.be/xKOanQKmGrY'
export const ROUND_3_FORM_VIEW_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSdP92wjxzALo2AiOF_TxEbgoeXBfxIvv55Vb4utpi7k9kMtpw/viewform'
export const ROUND_3_FORM_SHARE_URL = 'https://forms.gle/sjRF5uPLEZviKm8fA'
export const ROUND_3_YOUTUBE_PUBLISH = '2026-10-19T07:00:00+05:30'

export const PLACEHOLDER = {
  round1Video: '{{ROUND_1_VIDEO_URL}}',
  round1Form: '{{ROUND_1_FORM_URL}}',
  round2Video: '{{ROUND_2_VIDEO_URL}}',
  round2Form: '{{ROUND_2_FORM_URL}}',
  round3Video: '{{ROUND_3_VIDEO_URL}}',
  round3Form: '{{ROUND_3_FORM_URL}}',
} as const

export const TRIAL_FORM_DIRECT =
  'https://docs.google.com/forms/d/e/1FAIpQLSc1VZsru8taaKSuVzGxWYMorWgG_D0Zby8254_azPKQYepJzw/viewform?usp=publish-editor'

/**
 * Calibrated 08 Oct 2026 against trial form at 390 / 820 / 1280px
 * (+ spare + submit-confirmation buffer). Re-test each final round Form after URL swap.
 */
export const FORM_HEIGHTS = {
  formHeightMobile: 2300,
  formHeightTablet: 2000,
  formHeightDesktop: 1900,
} as const

const SHARED_COPY: Pick<
  NavratriGiveawayConfig,
  | 'privacyTextTa'
  | 'rulesSummaryTa'
  | 'rulesTextTa'
  | 'winnerAnnouncementTextTa'
  | 'recommendedContentIds'
  | 'recommendedYoutube'
> = {
  privacyTextTa:
    'உங்கள் பெயர், மொபைல் எண் மற்றும் ஊர் போன்ற தகவல்கள் இந்த நவராத்திரி போட்டி நிர்வாகத்திற்கும், தேவையானால் வெற்றியாளரை தொடர்பு கொள்ளவும் மட்டுமே பயன்படுத்தப்படும். தேவையற்ற தனிப்பட்ட தகவல்களை இந்த Form-ல் கேட்க மாட்டோம்.',
  rulesSummaryTa:
    'ஒரே நபர் ஒரே Round-இல் ஒரு முறை மட்டும் பங்கேற்க வேண்டும். முழுமையான போட்டி விதிமுறைகள் இந்தப் பக்கத்தின் “விதிமுறைகள்” பகுதியில் வழங்கப்படும்.',
  rulesTextTa:
    'பங்கேற்பு விதிமுறைகள் உரிமையாளர் அங்கீகாரத்திற்குப் பிறகு இங்கு விரிவாகச் சேர்க்கப்படும்.',
  winnerAnnouncementTextTa:
    'மூன்று சுற்றுகளிலும் பங்கேற்ற அனைவருக்கும் நன்றி. வெற்றியாளர் / அடுத்த அறிவிப்பு தொடர்பான தகவல் இப்பக்கத்திலும் தெய்வீக ஓசை YouTube சேனலிலும் பகிரப்படும்.',
  recommendedContentIds: [
    'narayani-stuti',
    'aigiri-nandini',
    'kanakadhara-stotram',
    'mahalakshmi-ashtakam',
    'abirami-16-blessings',
    'amman-potri-108',
  ],
  recommendedYoutube: [
    {
      titleTa: 'தேவி ஸ்துதி — பக்தி கேட்பு',
      titleEn: 'Devi Stuti',
      videoId: 'KtOA_MAX32M',
    },
  ],
}

/** Production — AUTO schedule; placeholders fail closed until mini URL handoff. */
export const NAVRATRI_GIVEAWAY: NavratriGiveawayConfig = {
  pageMode: 'auto',
  timezone: 'Asia/Kolkata',
  checkIntervalMs: 30_000,
  resyncClockMs: 300_000,
  rounds: [
    {
      label: 'ROUND 1',
      displayDateTa: 'அக்டோபர் 12, காலை 7:00 மணி',
      start: '2026-10-12T07:00:00+05:30',
      end: '2026-10-12T23:59:59+05:30',
      youtubePublish: ROUND_1_YOUTUBE_PUBLISH,
      videoUrl: ROUND_1_VIDEO_URL,
      formUrl: ROUND_1_FORM_VIEW_URL,
      formShareUrl: ROUND_1_FORM_SHARE_URL,
      ...FORM_HEIGHTS,
    },
    {
      label: 'ROUND 2',
      displayDateTa: 'அக்டோபர் 16, காலை 7:00 மணி',
      start: '2026-10-16T07:00:00+05:30',
      end: '2026-10-17T07:00:00+05:30',
      youtubePublish: ROUND_2_YOUTUBE_PUBLISH,
      videoUrl: ROUND_2_VIDEO_URL,
      formUrl: ROUND_2_FORM_VIEW_URL,
      formShareUrl: ROUND_2_FORM_SHARE_URL,
      ...FORM_HEIGHTS,
    },
    {
      label: 'ROUND 3',
      displayDateTa: 'அக்டோபர் 19, காலை 7:00 மணி',
      start: '2026-10-19T07:00:00+05:30',
      end: '2026-10-20T07:00:00+05:30',
      youtubePublish: ROUND_3_YOUTUBE_PUBLISH,
      videoUrl: ROUND_3_VIDEO_URL,
      formUrl: ROUND_3_FORM_VIEW_URL,
      formShareUrl: ROUND_3_FORM_SHARE_URL,
      ...FORM_HEIGHTS,
    },
  ],
  ...SHARED_COPY,
}

/** Trial — always trial_live; ignore round calendar. */
export const NAVRATRI_TRIAL: NavratriGiveawayConfig = {
  ...SHARED_COPY,
  pageMode: 'trial_live',
  timezone: 'Asia/Kolkata',
  checkIntervalMs: 30_000,
  resyncClockMs: 300_000,
  rounds: [
    {
      label: 'TRIAL',
      displayDateTa: 'TRIAL FORM இப்போது திறந்துள்ளது',
      start: '',
      end: '',
      videoUrl: 'https://www.youtube.com/watch?v=-IfPr5ICd9g',
      formUrl: TRIAL_FORM_DIRECT,
      ...FORM_HEIGHTS,
    },
    NAVRATRI_GIVEAWAY.rounds[1],
    NAVRATRI_GIVEAWAY.rounds[2],
  ],
}

export function isConfiguredUrl(value: string | undefined): boolean {
  return typeof value === 'string' && /^https:\/\//i.test(value.trim()) && !value.includes('{{')
}

export function extractYouTubeId(rawUrl: string): string | null {
  try {
    const u = new URL(rawUrl.trim())
    if (u.hostname === 'youtu.be') return u.pathname.split('/').filter(Boolean)[0] || null
    if (u.hostname.includes('youtube.com')) {
      if (u.pathname === '/watch') return u.searchParams.get('v')
      const parts = u.pathname.split('/').filter(Boolean)
      if (['shorts', 'embed', 'live'].includes(parts[0] ?? '')) return parts[1] || null
    }
  } catch {
    /* invalid URL */
  }
  return null
}

export function resolveRoundVideoId(round: RoundConfig | null): string | null {
  if (!round?.videoUrl?.trim()) return null
  const v = round.videoUrl.trim()
  if (isConfiguredUrl(v)) return extractYouTubeId(v)
  return null
}

export function googleFormEmbedUrl(rawUrl: string): string | null {
  try {
    const u = new URL(rawUrl.trim())
    if (!u.hostname.endsWith('google.com')) return null
    if (!u.pathname.includes('/forms/')) return null
    u.search = ''
    u.searchParams.set('embedded', 'true')
    return u.toString()
  } catch {
    return null
  }
}

export function googleFormDirectUrl(rawUrl: string): string {
  try {
    const u = new URL(rawUrl.trim())
    u.searchParams.delete('embedded')
    return u.toString()
  } catch {
    return rawUrl
  }
}

/** Prefer short share link (forms.gle) for fallback; otherwise full viewform URL. */
export function resolveFormFallbackUrl(round: RoundConfig): string {
  const share = round.formShareUrl?.trim()
  if (share && isConfiguredUrl(share)) return share
  return googleFormDirectUrl(round.formUrl)
}

function parseIst(iso: string): number | null {
  if (!iso?.trim()) return null
  const t = Date.parse(iso)
  return Number.isNaN(t) ? null : t
}

export function formatIstDateTime(ms: number) {
  return new Intl.DateTimeFormat('ta-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(ms))
}

/**
 * Path /navratri-trial/ must always resolve TRIAL_LIVE.
 * Manual pageMode overrides apply before AUTO time logic (URL validation still enforced in UI).
 */
export function resolveNavratriState(
  config: NavratriGiveawayConfig,
  nowMs: number,
  options?: { forceTrialLive?: boolean },
): { state: NavratriState; roundIndex: number | null; liveRound: RoundConfig | null } {
  if (options?.forceTrialLive || config.pageMode === 'trial_live') {
    return { state: 'TRIAL_LIVE', roundIndex: 0, liveRound: config.rounds[0] }
  }

  const mode = config.pageMode
  if (mode === 'force_preview') {
    return { state: 'PRE_EVENT', roundIndex: null, liveRound: null }
  }
  if (mode === 'force_r1') {
    return { state: 'ROUND_1_LIVE', roundIndex: 0, liveRound: config.rounds[0] }
  }
  if (mode === 'force_r2') {
    return { state: 'ROUND_2_LIVE', roundIndex: 1, liveRound: config.rounds[1] }
  }
  if (mode === 'force_r3') {
    return { state: 'ROUND_3_LIVE', roundIndex: 2, liveRound: config.rounds[2] }
  }
  if (mode === 'force_completed') {
    return { state: 'COMPLETED', roundIndex: null, liveRound: null }
  }

  const [r1, r2, r3] = config.rounds
  const r1s = parseIst(r1.start)
  const r1e = parseIst(r1.end)
  const r2s = parseIst(r2.start)
  const r2e = parseIst(r2.end)
  const r3s = parseIst(r3.start)
  const r3e = parseIst(r3.end)

  if (r1s == null || nowMs < r1s) {
    return { state: 'PRE_EVENT', roundIndex: null, liveRound: null }
  }
  if (r1e != null && nowMs < r1e) {
    return { state: 'ROUND_1_LIVE', roundIndex: 0, liveRound: r1 }
  }
  if (r2s == null || nowMs < r2s) {
    return { state: 'BETWEEN_1_2', roundIndex: null, liveRound: null }
  }
  if (r2e != null && isRoundWindowLive(r2.start, r2.end, nowMs)) {
    return { state: 'ROUND_2_LIVE', roundIndex: 1, liveRound: r2 }
  }
  if (r3s == null || nowMs < r3s) {
    return { state: 'BETWEEN_2_3', roundIndex: null, liveRound: null }
  }
  if (r3e != null && isRoundWindowLive(r3.start, r3.end, nowMs)) {
    return { state: 'ROUND_3_LIVE', roundIndex: 2, liveRound: r3 }
  }
  return { state: 'COMPLETED', roundIndex: null, liveRound: null }
}

/** Live round needs both video and Form URLs configured — never show stale partial embeds. */
export function isLiveRoundReady(round: RoundConfig | null): boolean {
  if (!round) return false
  const videoId = resolveRoundVideoId(round)
  const formEmbed = isConfiguredUrl(round.formUrl) ? googleFormEmbedUrl(round.formUrl) : null
  return Boolean(videoId && formEmbed)
}

/** Trial may show Form without video. */
export function isTrialFormReady(round: RoundConfig | null): boolean {
  if (!round) return false
  return Boolean(isConfiguredUrl(round.formUrl) && googleFormEmbedUrl(round.formUrl))
}

/**
 * Live window is half-open: start inclusive, end exclusive.
 * 16 Oct 23:59 and 17 Oct 06:59 stay LIVE. 17 Oct 07:00:00 is closed.
 */
export function isRoundWindowLive(startIso: string, endIso: string, now = Date.now()) {
  const start = Date.parse(startIso)
  const end = Date.parse(endIso)
  if (Number.isNaN(start) || Number.isNaN(end)) return false
  return now >= start && now < end
}

/** Authoritative campaign clock — absolute epoch ms vs ISO +05:30 timestamps (no server Date sync). */
export function navratriNowMs(): number {
  return Date.now()
}

export function trackNavratri(event: string, params?: Record<string, string | number | boolean>) {
  const w = window as Window & { gtag?: (...args: unknown[]) => void }
  w.gtag?.('event', event, params)
}

export const NAVRATRI_SEO = {
  production: {
    title: 'நவராத்திரி பரிசுப் போட்டி 2026 | Navratri Giveaway | Deiveega Oosai',
    description:
      'தெய்வீக ஓசை நவராத்திரி 2026 பரிசுப் போட்டி — Round 1, 2, 3 தேதிகள், போட்டி வீடியோ, Google Form மற்றும் நவராத்திரி பக்திப் பாடல்கள்.',
    path: '/navratri/',
  },
  trial: {
    title: 'Navratri Trial – noindex',
    description: 'Internal trial page for Navratri giveaway form calibration.',
    path: '/navratri-trial/',
  },
} as const

export const LIVE_UNAVAILABLE_COPY_TA =
  'இந்த சுற்று தொடங்குகிறது. பதிவு படிவம் சில நிமிடங்களில் கிடைக்கும். தயவுசெய்து பக்கத்தை மீண்டும் திறக்கவும்.'
